import { RequiredDocumentTypeEnum } from "@repo/core/enums/proposals";
import { PreProposalData } from "../../../store/preProposalStore";
import {
  UnicoDocumentProcessResponse,
  UnicoCreateProcessResponse
} from "@repo/core/services/entities/Unico";
import { DocumentUploadParams } from "@repo/core/screens/PreProposal/screens/BiometryValidation/types";

/**
 * Returns the proposal document id from an upload API body (`lngDocumento`),
 * when present and non-empty. Accepts numeric or string values from the backend.
 */
export const extractLngDocumentoFromUploadResponse = (
  payload: unknown
): string | undefined => {
  if (
    payload === null ||
    typeof payload !== "object" ||
    Array.isArray(payload)
  ) {
    return undefined;
  }

  if (!("lngDocumento" in payload)) {
    return undefined;
  }

  const { lngDocumento } = payload as { lngDocumento: unknown };
  if (lngDocumento === undefined || lngDocumento === null) {
    return undefined;
  }

  const id = String(lngDocumento).trim();
  return id.length > 0 ? id : undefined;
};

interface UploadDocumentParams {
  strTipoDocumento: string;
  strObservacoes: string;
  strCliente: string;
  strCpfPortador: string;
  strAutorizacao: string;
  datDocumentoExterno: string;
  strVisualizarWEB: string;
  contentType?: string;
}

export const createDocumentUploadPayload = (
  type: RequiredDocumentTypeEnum,
  proposalData: PreProposalData | null,
  metadata?: {
    observacoes?: string;
    contentType?: string;
  }
): Omit<DocumentUploadParams, "lngProposta" | "base64"> => {
  const observacoesMap: Record<RequiredDocumentTypeEnum, string> = {
    [RequiredDocumentTypeEnum.DOCUMENT_FRONT]:
      "Documento de biometria - Unico (Frente)",
    [RequiredDocumentTypeEnum.DOCUMENT_BACK]:
      "Documento de biometria - Unico (Verso)",
    [RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT]:
      "DOC IDENTIFICACAO -  REUTILIZADO UNICO",
    [RequiredDocumentTypeEnum.BIOMETRY_FACE]: "Selfie de biometria - Unico",
    [RequiredDocumentTypeEnum.BIOMETRY]: "Selfie de biometria - Unico"
  };

  return {
    strTipoDocumento: type,
    strObservacoes: metadata?.observacoes || observacoesMap[type],
    strCliente: proposalData?.strCliente || "",
    strCpfPortador: proposalData?.strCliente || "",
    strAutorizacao: "S",
    datDocumentoExterno: new Date().toISOString(),
    strVisualizarWEB: "S",
    contentType:
      metadata?.contentType ||
      (type === RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT
        ? "application/pdf"
        : "image/jpeg")
  };
};

export const uploadDocument = async (
  type: RequiredDocumentTypeEnum,
  base64: string,
  proposalData: PreProposalData | null,
  uploadFn: (
    lngProposta: number,
    base64: string,
    params: Omit<DocumentUploadParams, "lngProposta" | "base64">
  ) => Promise<unknown>,
  metadata?: {
    observacoes?: string;
    contentType?: string;
  }
): Promise<string | undefined> => {
  if (!proposalData?.lngProposta) {
    throw new Error("ID da proposta não encontrado");
  }

  const params = createDocumentUploadPayload(type, proposalData, metadata);

  const uploadResponse = await uploadFn(
    proposalData.lngProposta,
    base64,
    params
  );
  return extractLngDocumentoFromUploadResponse(uploadResponse);
};

export const uploadUnicoDocuments = async (
  documentData: UnicoDocumentProcessResponse,
  processData: UnicoCreateProcessResponse,
  proposalData: PreProposalData | null,
  uploadFn: (
    lngProposta: number,
    base64: string,
    params: UploadDocumentParams
  ) => Promise<unknown>,
  markDocumentAsUploaded: (type: RequiredDocumentTypeEnum) => void
): Promise<{
  uploadedFront: boolean;
  uploadedBack: boolean;
  uploadedSelfie: boolean;
  uploadedIdentityTypes: string[];
  reusedIdentityUploadFailed: boolean;
  /** `lngDocumento` from the BIOMETRY (selfie) upload only — used as `strNumeroDocumento` for Único `SELFIE` RegistrarConsulta */
  lngDocumentoForSelfieRegistration?: string;
}> => {
  const results = {
    uploadedFront: false,
    uploadedBack: false,
    uploadedSelfie: false
  };
  const uploadedIdentityTypes: string[] = [];
  const reuseBase64 = documentData.fullDocumentBase64?.trim();
  let reusedIdentityUploadFailed = false;
  let lngDocumentoSelfie: string | undefined;

  if (reuseBase64) {
    try {
      await uploadDocument(
        RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT,
        reuseBase64,
        proposalData,
        uploadFn,
        {
          contentType: documentData.fullDocumentContentType || "application/pdf"
        }
      );
      markDocumentAsUploaded(RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT);
      uploadedIdentityTypes.push(
        RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT
      );
    } catch (error) {
      console.error("Error uploading reused identity document (0017):", error);
      reusedIdentityUploadFailed = true;
    }
  } else {
    if (documentData.documentFrontBase64) {
      try {
        await uploadDocument(
          RequiredDocumentTypeEnum.DOCUMENT_FRONT,
          documentData.documentFrontBase64,
          proposalData,
          uploadFn
        );
        markDocumentAsUploaded(RequiredDocumentTypeEnum.DOCUMENT_FRONT);
        uploadedIdentityTypes.push(RequiredDocumentTypeEnum.DOCUMENT_FRONT);
        results.uploadedFront = true;
      } catch (error) {
        console.error("Error uploading document front:", error);
      }
    }

    if (documentData.documentBackBase64) {
      try {
        await uploadDocument(
          RequiredDocumentTypeEnum.DOCUMENT_BACK,
          documentData.documentBackBase64,
          proposalData,
          uploadFn
        );
        markDocumentAsUploaded(RequiredDocumentTypeEnum.DOCUMENT_BACK);
        uploadedIdentityTypes.push(RequiredDocumentTypeEnum.DOCUMENT_BACK);
        results.uploadedBack = true;
      } catch (error) {
        console.error("Error uploading document back:", error);
      }
    }
  }

  if (processData.selfie?.fileContents) {
    try {
      lngDocumentoSelfie = await uploadDocument(
        RequiredDocumentTypeEnum.BIOMETRY,
        processData.selfie.fileContents,
        proposalData,
        uploadFn,
        {
          contentType: processData.selfie.contentType
        }
      );
      markDocumentAsUploaded(RequiredDocumentTypeEnum.BIOMETRY);
      uploadedIdentityTypes.push(RequiredDocumentTypeEnum.BIOMETRY);
      results.uploadedSelfie = true;
    } catch (error) {
      console.error("Error uploading selfie:", error);
    }
  }

  return {
    ...results,
    uploadedIdentityTypes,
    reusedIdentityUploadFailed,
    ...(lngDocumentoSelfie !== undefined
      ? { lngDocumentoForSelfieRegistration: lngDocumentoSelfie }
      : {})
  };
};
